/** A captured fix, in the shape `ServicesRendered` stores it. */
export type CapturedPosition = { latitude: number; longitude: number };

/**
 * How long to wait for a fix before giving up on it.
 *
 * A technician changing status is mid-job and the save has to feel immediate,
 * so this is deliberately short. Missing the fix costs the visit its
 * ServiceChannel check-in; blocking the save on it would cost the status
 * change itself, which is worse.
 */
const POSITION_TIMEOUT_MS = 5_000;

/**
 * A fix this old is still good enough. The technician has not moved far in a
 * minute, and reusing a cached fix avoids re-acquiring on every status change
 * — which for a check-in followed by a check-out is two acquisitions minutes
 * apart at the same address.
 */
const POSITION_MAX_AGE_MS = 60_000;

/**
 * The technician's current position, or null.
 *
 * Coarse on purpose: `enableHighAccuracy` is off, so this uses wifi and cell
 * rather than waking the GPS. ServiceChannel's geofence is **half a mile**
 * (verified on their sandbox — 700m accepted, 900m rejected with
 * `ErrorCode 941`), and a wifi-assisted fix is comfortably inside that in
 * built-up areas. Rural cell-only fixes may not be, which the backend handles:
 * it validates the coordinate with ServiceChannel before checking in, and
 * falls back to a plain status push when the fix missed.
 *
 * **Never throws and never rejects.** Every failure — permission denied, no
 * hardware, timed out, insecure origin, browser without the API — resolves to
 * null. A location is an enhancement to a status change, never a precondition
 * for one.
 */
export const captureCoarsePosition = (): Promise<CapturedPosition | null> =>
  new Promise((resolve) => {
    // Absent on insecure origins and in older embedded webviews, where reading
    // it at all would throw rather than return undefined.
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null);
      return;
    }

    // Belt and braces: some webviews neither call back nor error, and a
    // technician waiting on a hung save is the failure this is meant to avoid.
    let settled = false;
    const finish = (value: CapturedPosition | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const guard = setTimeout(() => finish(null), POSITION_TIMEOUT_MS + 1_000);

    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          clearTimeout(guard);
          const { latitude, longitude } = pos.coords;
          // 0,0 is in the Gulf of Guinea, and the backend reads it as "no
          // coordinate". Treating it as absent here keeps the two ends
          // agreeing rather than sending a fix that means nothing.
          if (!latitude || !longitude) {
            finish(null);
            return;
          }
          finish({ latitude, longitude });
        },
        () => {
          clearTimeout(guard);
          finish(null);
        },
        {
          enableHighAccuracy: false,
          timeout: POSITION_TIMEOUT_MS,
          maximumAge: POSITION_MAX_AGE_MS,
        },
      );
    } catch {
      clearTimeout(guard);
      finish(null);
    }
  });

/**
 * Whether the browser will hand us a position without asking, will ask, or
 * has been told no.
 *
 * `denied` is the one state that matters to the On Call gate (86bc18e7q): a
 * ServiceChannel check-in needs a coordinate, and a denied permission is the
 * one thing the technician can fix themselves, so it is the one thing allowed
 * to hold On Call back. `unknown` covers browsers without the Permissions API
 * for geolocation (older Safari): the gate stays open and a capture is simply
 * attempted on tap, as it always was.
 */
export type GeolocationPermission = 'granted' | 'prompt' | 'denied' | 'unknown';

export const queryGeolocationPermission = async (): Promise<GeolocationPermission> => {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return 'denied';
  }
  if (!navigator.permissions?.query) {
    return 'unknown';
  }
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' });
    if (status.state === 'granted' || status.state === 'prompt' || status.state === 'denied') {
      return status.state;
    }
    return 'unknown';
  } catch {
    // Some engines throw for permission names they do not model.
    return 'unknown';
  }
};

/**
 * Triggers the browser's location prompt. The web platform cannot open the
 * device's settings, so when the permission is already denied this resolves to
 * the same denied state and the caller has to tell the technician where to
 * turn it on. Resolves to the permission state afterwards.
 */
export const requestGeolocationPermission = async (): Promise<GeolocationPermission> => {
  await captureCoarsePosition();
  return queryGeolocationPermission();
};
